import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { usePlan } from "../context/PlanContext";
import { Lock, Sparkles, ArrowRight, X, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_MESSAGES = {
  analytics: {
    title: "Business Analytics",
    desc: "Track your business performance with analytics. Upgrade to unlock.",
    icon: "chart",
  },
  gst_reports: {
    title: "GST Reports",
    desc: "Generate GST-ready reports instantly. Upgrade to continue.",
    icon: "file",
  },
  ca_collaboration: {
    title: "CA/Professional Collaboration",
    desc: "Work seamlessly with your accountant. Upgrade to enable collaboration.",
    icon: "users",
  },
  full_history: {
    title: "Full Invoice History",
    desc: "Only last 50 invoices are accessible. Upgrade to view full history.",
    icon: "history",
  },
  document_workflows: {
    title: "Document Workflows",
    desc: "Manage documents with your CA. Upgrade to enable workflows.",
    icon: "file",
  },
  no_branding: {
    title: "Remove Branding",
    desc: "Remove InvoiceSetu branding from your invoices. Upgrade now.",
    icon: "badge",
  },
};

export function FeatureGate({ feature, children, blur = true }) {
  const { hasFeature, isFree, isTrial, isPaid } = usePlan();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const hasAccess = hasFeature(feature);

  if (hasAccess || isPaid || isTrial) {
    return <>{children}</>;
  }

  const msg = FEATURE_MESSAGES[feature] || {
    title: "Premium Feature",
    desc: "Upgrade to access this feature.",
  };

  const handleUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await axios.post(`${API}/subscription/unlock-feature`, { feature }, { withCredentials: true });
      if (res.data.success) {
        toast.success(res.data.message);
        setShowModal(false);
        window.location.reload();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unlock failed");
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className="relative" data-testid={`feature-gate-${feature}`}>
      {/* Blurred content */}
      <div className={blur ? "pointer-events-none select-none" : ""} style={blur ? { filter: "blur(4px)", opacity: 0.5 } : {}}>
        {children}
      </div>

      {/* Overlay */}
      <div
        className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-xl cursor-pointer z-10"
        onClick={() => setShowModal(true)}
        data-testid={`feature-gate-overlay-${feature}`}
      >
        <div className="text-center px-6 py-4 max-w-sm">
          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5 text-violet-600" />
          </div>
          <h3 className="text-sm font-bold text-slate-900 mb-1">{msg.title}</h3>
          <p className="text-xs text-slate-500 mb-3">{msg.desc}</p>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 bg-violet-50 px-3 py-1.5 rounded-full border border-violet-200">
            <Sparkles className="w-3 h-3" /> Upgrade to unlock
          </span>
        </div>
      </div>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4"
            onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-sm"
              data-testid="upgrade-modal"
            >
              <div className="p-6 text-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-lg font-bold text-slate-900 mb-1">{msg.title}</h2>
                <p className="text-sm text-slate-500 mb-5">{msg.desc}</p>

                <div className="space-y-2.5">
                  <Button
                    onClick={() => { setShowModal(false); navigate("/pricing"); }}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="upgrade-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" /> View Plans & Upgrade
                  </Button>

                  <Button
                    onClick={handleUnlock}
                    variant="outline"
                    disabled={unlocking}
                    className="w-full border-violet-200 text-violet-700 hover:bg-violet-50"
                    data-testid="unlock-btn"
                  >
                    {unlocking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                    Unlock free for 3 days
                  </Button>

                  <button onClick={() => setShowModal(false)} className="text-xs text-slate-400 hover:text-slate-600 mt-2">
                    Maybe later
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Inline upgrade nudge — subtle CTA within content
 */
export function UpgradeNudge({ message, feature, compact = false }) {
  const { hasFeature, isPaid, isTrial } = usePlan();
  const navigate = useNavigate();

  if (hasFeature(feature) || isPaid || isTrial) return null;

  if (compact) {
    return (
      <button
        onClick={() => navigate("/pricing")}
        className="inline-flex items-center gap-1 text-[10px] font-medium text-violet-600 hover:text-violet-800 bg-violet-50 px-2 py-0.5 rounded-full border border-violet-100 transition-colors"
        data-testid={`nudge-${feature}`}
      >
        <Sparkles className="w-2.5 h-2.5" /> {message || "Upgrade"}
      </button>
    );
  }

  return (
    <div
      className="flex items-center gap-3 p-3 bg-violet-50 rounded-xl border border-violet-100 cursor-pointer hover:bg-violet-100 transition-colors"
      onClick={() => navigate("/pricing")}
      data-testid={`nudge-${feature}`}
    >
      <Sparkles className="w-4 h-4 text-violet-500 flex-shrink-0" />
      <p className="text-xs text-violet-700 flex-1">{message || "Upgrade to unlock this feature"}</p>
      <ArrowRight className="w-3.5 h-3.5 text-violet-400" />
    </div>
  );
}
